import React from 'react';
import { remoteImageToDatUrl } from '@cp/utils/browser/imageToDataUrl';
import { prepareBase64Data } from '@cp/utils/data/prepareBase64';
import { base64toBlob } from '@cp/utils/browser/base64ToBlob';
import { handleApiServerError } from '@cp/utils/apiServer/errorHandler';
import { DEFAULT_MIME_TYPE } from '@common/const/mime';
import { PhotoPickerHandle } from '@cp/ds/src/components/photoPicker/model';
import { ApiServerError } from '@cp/utils/rtk/query/model';
import { SerializedError } from '@reduxjs/toolkit';

interface UseClientPhotoCreatorReturn {
  imageUrl: string | undefined;
  photoPickerRef: React.RefObject<PhotoPickerHandle>;
  isLoading: boolean;
  isServiceError: boolean;
  create: () => Promise<void>;
  setLoading: (newState: boolean) => void;
  setImageUrl: (newImageUrl: string | undefined) => void;
}

export const useClientPhotoCreator = <R>({
  onUpload,
  onUploadComplete,
  clientProfilePhotoNotCroppedOriginalSrc,
  apiServerErrorHandler = handleApiServerError,
}: {
  onUpload: (payload: { original: Blob; cropped: Blob }) => Promise<
    | {
        data: R;
      }
    | {
        error: ApiServerError | SerializedError;
      }
  >;
  onUploadComplete?: () => void | Promise<void>;
  clientProfilePhotoNotCroppedOriginalSrc?: string;
  apiServerErrorHandler?: typeof handleApiServerError;
}): UseClientPhotoCreatorReturn => {
  const photoPickerRef = React.useRef<PhotoPickerHandle>(null);
  const [isLoading, setLoading] = React.useState(false);
  const [isServiceError, setServiceError] = React.useState(false);
  const [imageUrl, setImageUrl] = React.useState(clientProfilePhotoNotCroppedOriginalSrc);

  React.useEffect(() => {
    if (clientProfilePhotoNotCroppedOriginalSrc) {
      setImageUrl(clientProfilePhotoNotCroppedOriginalSrc);
    }
  }, [clientProfilePhotoNotCroppedOriginalSrc]);

  const create = async () => {
    setLoading(true);
    if (!photoPickerRef.current) {
      throw new Error('photo picker is unavailable');
    }

    const croppedPhotoFromPicker = await photoPickerRef.current?.getCroppedImage();

    if (!croppedPhotoFromPicker) {
      return;
    }

    let photoOriginal = imageUrl || croppedPhotoFromPicker;

    if (photoOriginal && photoOriginal.startsWith('http')) {
      photoOriginal = await remoteImageToDatUrl(photoOriginal);
    }

    const original = prepareBase64Data(photoOriginal, { defaultMimeType: DEFAULT_MIME_TYPE });
    const cropped = prepareBase64Data(croppedPhotoFromPicker, { defaultMimeType: DEFAULT_MIME_TYPE });

    const response = await onUpload({
      original: base64toBlob(original.data, original.type),
      cropped: base64toBlob(cropped.data, cropped.type),
    });

    if ('error' in response) {
      apiServerErrorHandler(response.error);
      setServiceError(true);
    } else {
      await onUploadComplete?.();
      setLoading(false);
      setServiceError(false);
    }
  };

  return {
    imageUrl,
    create,
    isLoading,
    setLoading,
    isServiceError,
    setImageUrl,
    photoPickerRef,
  };
};
